import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import PropTypes from 'prop-types';
import React, { useState, useMemo } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Progress, Row, Col } from 'reactstrap';
import { pathComparator } from '@truqc/content-path';
import Badge from './badge';
// eslint-disable-next-line import/no-unresolved, import/extensions
import Icon from '../../../common/containers/icon';
import MetricCard from './metricCard';
import { formatTimestamp, formatShortDate, pct, getCheckStatus } from './qualityAgentHelpers';

// Prior run item component - compact summary that opens modal for details
const PriorRunItem = _ref => {
  var _summary$assessment;
  let run = _ref.run;
  const _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    isModalOpen = _useState2[0],
    setIsModalOpen = _useState2[1];
  const _run$summary = run.summary,
    summary = _run$summary === void 0 ? {} : _run$summary,
    _run$checks = run.checks,
    checks = _run$checks === void 0 ? {} : _run$checks;

  // Calculate pass percentage
  const passRate = pct(summary.passed || 0, summary.totalChecks || 0);

  // Get assessment color
  const getAssessmentColor = assessment => {
    if (!assessment) return 'secondary';
    if (assessment === 'passed' || assessment === 'good' || assessment === 'all_good') return 'success';
    if (assessment === 'needs_attention' || assessment === 'warning') return 'warning';
    return 'danger';
  };

  // Convert checks to array sorted by creation order
  const checksArray = useMemo(() => {
    const arr = Object.entries(checks).map(_ref2 => {
      let _ref3 = _slicedToArray(_ref2, 2),
        id = _ref3[0],
        check = _ref3[1];
      return _objectSpread({
        id
      }, check);
    });
    return arr.sort(pathComparator('_created'));
  }, [checks]);

  // Format assessment for display
  const assessmentDisplay = ((_summary$assessment = summary.assessment) === null || _summary$assessment === void 0 ? void 0 : _summary$assessment.replace(/_/g, ' ')) || 'Unknown';
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
    className: "border-bottom py-2 d-flex justify-content-between align-items-start",
    style: {
      cursor: 'pointer'
    },
    onClick: () => setIsModalOpen(true),
    role: "button",
    tabIndex: 0,
    onKeyDown: e => e.key === 'Enter' && setIsModalOpen(true)
  }, /*#__PURE__*/React.createElement("div", {
    className: "fw-medium",
    style: {
      fontSize: '0.875rem'
    }
  }, /*#__PURE__*/React.createElement("div", {
    className: "d-flex align-items-center",
    style: {
      gap: 8
    }
  }, /*#__PURE__*/React.createElement("span", null, formatShortDate(summary.timestamp)), /*#__PURE__*/React.createElement(Badge, {
    color: getAssessmentColor(summary.assessment),
    pill: true,
    style: {
      padding: '3px 10px',
      fontSize: '0.75rem'
    },
    className: "text-capitalize"
  }, assessmentDisplay)), /*#__PURE__*/React.createElement("div", {
    className: "d-flex align-items-center mt-1",
    style: {
      gap: 12
    }
  }, /*#__PURE__*/React.createElement("span", {
    className: "text-success"
  }, /*#__PURE__*/React.createElement(Icon, {
    glyph: "check",
    style: {
      marginRight: 4
    }
  }), summary.passed || 0), /*#__PURE__*/React.createElement("span", {
    className: "text-danger"
  }, /*#__PURE__*/React.createElement(Icon, {
    glyph: "xmark",
    style: {
      marginRight: 4
    }
  }), summary.failed || 0), /*#__PURE__*/React.createElement("span", {
    className: "text-warning"
  }, /*#__PURE__*/React.createElement(Icon, {
    glyph: "triangle-exclamation",
    style: {
      marginRight: 4
    }
  }), summary.warnings || 0))), /*#__PURE__*/React.createElement("span", {
    className: "text-muted"
  }, /*#__PURE__*/React.createElement(Icon, {
    glyph: "arrow-up-right-from-square"
  }))), /*#__PURE__*/React.createElement(Modal, {
    isOpen: isModalOpen,
    toggle: () => setIsModalOpen(false),
    size: "lg"
  }, /*#__PURE__*/React.createElement(ModalHeader, {
    toggle: () => setIsModalOpen(false),
    className: "bg-white"
  }, /*#__PURE__*/React.createElement("div", {
    className: "d-flex align-items-center"
  }, /*#__PURE__*/React.createElement("span", {
    style: {
      fontWeight: 'bold'
    }
  }, "Prior Run ", formatTimestamp(summary.timestamp)), /*#__PURE__*/React.createElement(Badge, {
    color: getAssessmentColor(summary.assessment),
    pill: true,
    style: {
      marginLeft: '0.75rem',
      padding: '4px 12px'
    },
    className: "text-capitalize"
  }, assessmentDisplay))), /*#__PURE__*/React.createElement(ModalBody, null, /*#__PURE__*/React.createElement(Row, {
    className: "g-3 mb-4"
  }, /*#__PURE__*/React.createElement(Col, {
    xs: 6,
    md: 3
  }, /*#__PURE__*/React.createElement(MetricCard, {
    title: "Total Checks",
    value: summary.totalChecks
  })), /*#__PURE__*/React.createElement(Col, {
    xs: 6,
    md: 3
  }, /*#__PURE__*/React.createElement(MetricCard, {
    title: "Passed",
    value: summary.passed,
    color: "success"
  })), /*#__PURE__*/React.createElement(Col, {
    xs: 6,
    md: 3
  }, /*#__PURE__*/React.createElement(MetricCard, {
    title: "Failed",
    value: summary.failed,
    color: "danger"
  })), /*#__PURE__*/React.createElement(Col, {
    xs: 6,
    md: 3
  }, /*#__PURE__*/React.createElement(MetricCard, {
    title: "Warnings",
    value: summary.warnings,
    color: "warning"
  }))), /*#__PURE__*/React.createElement("div", {
    className: "mb-4"
  }, /*#__PURE__*/React.createElement("div", {
    className: "d-flex justify-content-between small mb-1"
  }, /*#__PURE__*/React.createElement("span", {
    className: "text-muted"
  }, "Overall pass rate"), /*#__PURE__*/React.createElement("span", {
    className: "fw-medium"
  }, passRate, "%")), /*#__PURE__*/React.createElement(Progress, {
    value: passRate,
    color: (() => {
      if (passRate >= 80) return 'success';
      if (passRate >= 50) return 'warning';
      return 'danger';
    })(),
    style: {
      height: 8
    }
  })), /*#__PURE__*/React.createElement("hr", null), checksArray.length > 0 && /*#__PURE__*/React.createElement("div", {
    style: {
      maxHeight: '50vh',
      overflowY: 'auto'
    }
  }, checksArray.map(check => {
    var _check$result;
    const status = getCheckStatus(check);
    return /*#__PURE__*/React.createElement("div", {
      key: check.id,
      className: "d-flex align-items-start py-2 border-bottom",
      style: {
        gap: 12
      }
    }, /*#__PURE__*/React.createElement("span", {
      className: "flex-shrink-0 ".concat((() => {
        if (status === 'passed') return 'text-success';
        if (status === 'warning') return 'text-warning';
        return 'text-danger';
      })()),
      style: {
        fontSize: '1.1rem',
        lineHeight: 1.4
      }
    }, (() => {
      if (status === 'passed') return '✓';
      if (status === 'warning') return '⚠';
      return '✕';
    })()), /*#__PURE__*/React.createElement("div", {
      className: "flex-grow-1"
    }, /*#__PURE__*/React.createElement("div", {
      className: "fw-medium"
    }, check.description || check.id), ((_check$result = check.result) === null || _check$result === void 0 ? void 0 : _check$result.message) && /*#__PURE__*/React.createElement("div", {
      className: "text-muted small mt-1"
    }, check.result.message)));
  })), checksArray.length === 0 && /*#__PURE__*/React.createElement("div", {
    className: "text-muted text-center py-4"
  }, "No check details available")), /*#__PURE__*/React.createElement(ModalFooter, null, /*#__PURE__*/React.createElement(Button, {
    color: "secondary",
    onClick: () => setIsModalOpen(false)
  }, "Close"))));
};
PriorRunItem.propTypes = {
  run: PropTypes.shape({
    summary: PropTypes.shape({
      timestamp: PropTypes.string,
      assessment: PropTypes.string,
      totalChecks: PropTypes.number,
      passed: PropTypes.number,
      failed: PropTypes.number,
      warnings: PropTypes.number
    }),
    checks: PropTypes.shape({})
  }).isRequired
};
export default PriorRunItem;